import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';

import { api } from '@/lib/api';
import { PAYMENT_METHOD_LABELS, dayFr, gnf } from '@/lib/invoices';
import { requirePermission } from '@/lib/route-guards';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { QueryError } from '@/components/QueryError';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

import type { Client, CustomerPayment, UUID } from '@logistech/ui-shared';

export const Route = createFileRoute('/_authed/payments')({
  beforeLoad: () => requirePermission('invoices.read'),
  component: PaymentsPage,
});

/** Ligne de GET /customer-invoices/payments : encaissement joint à sa facture et à son client. */
interface PaymentRow extends CustomerPayment {
  invoiceId: UUID;
  invoice: { id: UUID; number: string | null };
  client: { id: UUID; name: string } | null;
  createdAt: string;
}

interface PaymentPage { data: PaymentRow[]; total: number; page: number; pageSize: number }

const ALL = 'all';
const PAGE_SIZE = 50;

export function PaymentsPage() {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [method, setMethod] = useState(ALL);
  const [clientId, setClientId] = useState(ALL);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  // Tout changement de filtre ramène à la première page
  const filter = <T,>(set: (v: T) => void) => (v: T) => { set(v); setPage(1); };

  const { data: clients } = useQuery({
    queryKey: ['clients', { showInactive: false }],
    queryFn: async () => (await api.get<Client[]>('/clients')).data,
  });

  const query = useQuery({
    queryKey: ['customer-payments', { from, to, method, clientId, search, page }],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (method !== ALL) params.set('method', method);
      if (clientId !== ALL) params.set('clientId', clientId);
      if (search.trim()) params.set('search', search.trim());
      return (await api.get<PaymentPage>(`/customer-invoices/payments?${params}`)).data;
    },
    placeholderData: keepPreviousData,
  });
  const { data, isLoading } = query;
  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold">Paiements clients</h1>
      <QueryError query={query} />

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label htmlFor="pay-from">Du</Label>
          <Input id="pay-from" type="date" value={from} max={to || undefined} onChange={(e) => filter(setFrom)(e.target.value)} />
        </div>
        <div>
          <Label htmlFor="pay-to">Au</Label>
          <Input id="pay-to" type="date" value={to} min={from || undefined} onChange={(e) => filter(setTo)(e.target.value)} />
        </div>
        <Select value={method} onValueChange={filter(setMethod)}>
          <SelectTrigger className="w-44" aria-label="Méthode"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Toutes les méthodes</SelectItem>
            {Object.entries(PAYMENT_METHOD_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={clientId} onValueChange={filter(setClientId)}>
          <SelectTrigger className="w-56" aria-label="Client"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les clients</SelectItem>
            {clients?.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative w-56">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Référence…"
            aria-label="Référence"
            className="pl-9"
            value={search}
            onChange={(e) => filter(setSearch)(e.target.value)}
          />
        </div>
      </div>

      <div className="bg-card rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Date</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Facture</TableHead>
              <TableHead>Méthode</TableHead>
              <TableHead className="text-right">Montant</TableHead>
              <TableHead>Référence</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && Array.from({ length: 4 }).map((_, i) => (
              <TableRow key={i}>{Array.from({ length: 6 }).map((__, j) => <TableCell key={j}><Skeleton className="h-4" /></TableCell>)}</TableRow>
            ))}
            {data?.data.map((p) => (
              <TableRow key={p.id}>
                <TableCell>{dayFr(p.paidAt)}</TableCell>
                <TableCell>{p.client?.name ?? '—'}</TableCell>
                <TableCell>
                  <Link to="/invoices/$id" params={{ id: p.invoiceId }} className="font-mono font-semibold text-primary-500 hover:underline">
                    {p.invoice.number ?? 'Brouillon'}
                  </Link>
                </TableCell>
                <TableCell><Badge variant="secondary">{PAYMENT_METHOD_LABELS[p.method]}</Badge></TableCell>
                <TableCell className="text-right">{gnf(p.amount)}</TableCell>
                <TableCell className="font-mono text-xs">{p.reference ?? '—'}</TableCell>
              </TableRow>
            ))}
            {data && data.data.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                  <p>Aucun paiement</p>
                  <Button asChild variant="link">
                    <Link to="/invoices">Enregistrer un paiement depuis une facture</Link>
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {data && data.total > 0 && (
        <div className="flex items-center justify-end gap-3 text-sm">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Précédent</Button>
          <span>Page {page}/{pages}</span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Suivant</Button>
        </div>
      )}
    </div>
  );
}
